import {Injectable} from '@angular/core';
import {HttpService} from '@app/core/services/http.service';
import {AuthService} from '@app/core/services/auth.service';
import {Availability} from '@app/core/models/availability.model';
import {BaseService} from '@app/core/services/base.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Moment} from 'moment';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {Pagination, PaginationHolder} from '@app/core/models/pagination.model';

interface IGetAvailabilitiesUsersOnDayResponse {
	approved_availabilities_user_id: number[];
	pending_availabilities_user_id: number[];
}

export interface IGetAvailabilitiesParams {
	include_extra?: boolean;
	'filter[user_id]'?: string;
}

export interface IPostAvailabilityPayload {
	start: Moment;
	end: Moment;
	reason: string;
	user: { id: number };
}

@Injectable()
export class AvailabilityService extends BaseService {
	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
	) {
		super(authService, interService, httpService, store);
	}

	public fresh(params?): Availability {
		if (!params) {
			return new Availability(this.tenantTZ, this.interService);
		}

		return new Availability(this.tenantTZ, this.interService).deserialize(params);
	}

	public getUserAvailabilities(filters: IGetAvailabilitiesParams = {}, page_size: number = 200, page: number = 1): Observable<PaginationHolder<Availability>> {
		const data = {
			...filters,
			sort: '-start',
			page_size,
			page,
		};

		return this.httpService.getJson<PaginationHolder<Availability>>('users/availabilities', data).pipe(map((res: any) => {
			res.data = res.data.map(availability => new Availability(this.tenantTZ, this.interService).deserialize(availability));
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getAvailabilities(filters: IGetAvailabilitiesParams = {}, page_size: number = 200, page: number = 1): Observable<PaginationHolder<Availability>> {
		const data = {
			...filters,
			sort: '-start',
			page_size,
			page,
		};

		return this.httpService.getJson<PaginationHolder<Availability>>('actions/availabilities', data).pipe(map((res: any) => {
			res.data = res.data.map(availability => new Availability(this.tenantTZ, this.interService).deserialize(availability));
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getAvailabilitiesInInterval(data: { start_date: Moment, end_date: Moment }): Observable<Availability[]> {
		return this.httpService.getJson<Availability[]>('users/availabilities/inInterval', {
			start_date: data.start_date.format('YYYY-MM-DD'),
			end_date: data.end_date.format('YYYY-MM-DD'),
		}).pipe(map(availabilities => availabilities.map(availability => new Availability(this.tenantTZ, this.interService).deserialize(availability))));
	}

	public post(payload: IPostAvailabilityPayload): Observable<Availability> {
		return this.httpService.postJson<Availability>('users/availabilities', {
			start: payload.start.format('YYYY-MM-DD'),
			end: payload.end.format('YYYY-MM-DD'),
			reason: payload.reason,
			user: payload.user,
		}).pipe(map(availability => new Availability(this.tenantTZ, this.interService).deserialize(availability)));
	}

	public delete(id: number): Observable<Availability> {
		return this.httpService.deleteJson<Availability>('users/availabilities/' + id)
			.pipe(map(availability => new Availability(this.tenantTZ, this.interService).deserialize(availability)));
	}

	public approve(id: number): Observable<Availability> {
		return this.httpService.patchJson('users/availabilities/' + id + '/approve', {});
	}

	public reject(id: number): Observable<Availability> {
		return this.httpService.patchJson('users/availabilities/' + id + '/reject', {});
	}
}
